import { ImResponse, leyanbotClient, doorClient, resolve } from 'services/api';

export interface GetSupplementKnowledgeListParams {
  source?: SupplementKnowledge.Source;
  searchType?: SupplementKnowledge.SearchType;
  reviewStatus?:
    | SupplementKnowledge.ReviewStatus[]
    | SupplementKnowledge.AiErrorReplyReviewStatus[];
  searchText?: string;
  startTimeStamp?: number;
  endTimeStamp?: number;
  userIds?: number[];
  storeIds?: number[];
  platformStoreScope?: General.StoreScope[];
  pageNo: number;
  pageSize: number;
  orderBy?: 'sales' | 'times' | 'reviewed_at';
  knowledgeId?: number;
  spuList?: SupplementKnowledge.FilterSPU[];
  /**
   * 审核人
   * -1 为待分配
   */
  reviewerIds?: number[];
  /**
   * 审核开始时间
   */
  reviewStartTimeStamp?: number;
  /**
   * 审核结束时间
   */
  reviewEndTimeStamp?: number;
}

export interface SupplementKnowledgeRelatedSPU {
  platform: string;
  shopId: string;
  region: string;
  spuId: string;
  shopName: string;
  shopAlias: string;
  title: string;
  imageUrl: string;
  itemUrl: string;
  skus: {
    sellerSku: string;
    skuId: string;
    skuName: string;
  }[];
  itemInternalId: number;
  sellerSpu: string;
  storeId: number;
  price: number;
  unit: string;
  allSkus?: {
    sellerSku: string;
    skuId: string;
    skuName: string;
  }[];
}

export interface SupplementalKnowledgeValueMessage {
  messageId: string;
  message: string;
  translateMap: Record<string, string>;
}

export interface SupplementalKnowledgeValue {
  rewriteQuestion: string;
  rewriteQuestionTranslateMap: Record<string, string>;
  question?: SupplementalKnowledgeValueMessage;
  originQuestions?: SupplementalKnowledgeValueMessage[];
  answer: SupplementKnowledgeAnswer;
  answers: SupplementKnowledgeAnswer[];
  originAnswers: SupplementKnowledgeAnswer[];
  rewriteAnswers?: SupplementKnowledgeAnswer[];
  buyerNick: string;
  skuIds: string[];
  botQaMap?: Record<string, SupplementKnowledgeValueMessage[]>;
}

export interface SupplementalKnowledgeBindItem {
  imageUrl?: string;
  itemInternalId?: number;
  platform: General.PlatformUpperCase;
  region: General.UpperCaseRegion;
  shopId: string;
  spuId: string;
  title?: string;
  skus: {
    skuId: string;
    skuName: string;
    sellerSku: string;
  }[];
}

export interface SupplementalKnowledgeAiReplyKnowledge {
  knowledgeId?: number;
  key: string;
  value: SupplementKnowledgeAnswer[];
  score: string;
  source:
    | '客服提交答案'
    | 'AI运营_商品知识_商品属性'
    | '商品详情页'
    | '商品详情页_人工修改'
    | '商家外部知识导入'
    | '商家线上文档同步'
    | '主管提交历史未回复问题答案'
    | 'CIH提交历史未回复问题答案';
  updateTime: number;
  syncTime: number;
  updateUser: string;
  deleted: boolean;
  modify: boolean;
  sourceUrl: string;
  sourceName: string;
  bindItem: SupplementalKnowledgeBindItem[];
  kvId?: number;
  pointId: string;
  sourceId: string;
}

export type CiteKnowledge = Omit<
  SupplementalKnowledgeAiReplyKnowledge,
  | 'sourceName'
  | 'sourceUrl'
  | 'syncTime'
  | 'updateUser'
  | 'kvId'
  | 'pointId'
  | 'deleted'
  | 'modify'
  | 'sourceId'
>;

export type SourceKnowledge = Omit<SupplementalKnowledgeAiReplyKnowledge, 'score'>;

export interface SupplementalKnowledgeAiReply {
  replyContent: SupplementKnowledgeAnswer[];
  replyTime: number;
  testReqId: string;
  citeKnowledge: CiteKnowledge[];
  sourceKnowledge: SourceKnowledge[];
  req?: string;
}

export interface SupplementKnowledgeStoreInfo {
  storeId: number;
  storeType: number;
  storeName: string;
  region: string;
  externalId: string;
  externalStoreId: string;
  alias: string;
  sellerType: number;
  externalType: string;
  is3Pf: boolean;
  memoizedIsInitialized: number;
  unknownFields: {
    fields: Record<string, unknown>;
  };
  memoizedSize: number;
  memoizedHashCode: number;
}

export interface SupplementKnowledgeData {
  id: number;
  question: string;
  answer: string;
  storeInfo?: SupplementKnowledgeStoreInfo;
  submitItemIdentifier: SupplementKnowledgeRelatedSPU;
  aiReviewStatus: SupplementKnowledge.AiReviewStatus;
  manualReviewStatus: SupplementKnowledge.ManualReviewStatus;
  reviewStatus: SupplementKnowledge.ReviewStatus | SupplementKnowledge.AiErrorReplyReviewStatus;
  submittedTime: number;
  submittedUserName: string;
  // 是否可以保存并启用
  enable: boolean;
  supplementalKnowledgeValue: SupplementalKnowledgeValue;
  originalCount?: {
    spuCount: number;
    skuCount: number;
  };
  expandCount?: {
    spuCount: number;
    skuCount: number;
  };
  updateTime: number;
  updateType:
    | 'SUBMIT_KNOWLEDGE'
    | 'UPDATE_ANSWER'
    | 'UPDATE_STATUS_PASSED'
    | 'UPDATE_STATUS_FAILED'
    | 'SUBMIT_REMARK'
    | 'UPDATE_SPU_SKU';
  reviewedTime: number;
  reviewers: string[];
}

export interface GetSupplementKnowledgeListResponse {
  supplementalKnowledgeList: SupplementKnowledgeData[];
  totalCount: number;
}

export const getSupplementKnowledgeList = (
  params: GetSupplementKnowledgeListParams,
  signal?: AbortSignal,
) => {
  return leyanbotClient.post<ImResponse<GetSupplementKnowledgeListResponse>>(
    '/oversea-knowledge/supplemental_knowledge/list',
    params,
    {
      signal,
    },
  );
};

export const getSupplementKnowledgeListItem = (knowledgeId: number, signal?: AbortSignal) => {
  return leyanbotClient
    .post<ImResponse<GetSupplementKnowledgeListResponse>>(
      '/oversea-knowledge/supplemental_knowledge/list',
      {
        knowledgeId,
        pageNo: 1,
        pageSize: 1,
      },
      {
        signal,
      },
    )
    .then((res) => res.data.data?.supplementalKnowledgeList?.[0]);
};

export interface SupplementKnowledgeDetailTimeline {
  userName: string;
  createTime: number;
  content: string;
  operationType: SupplementKnowledge.OperationType;
}

export interface SupplementKnowledgeDetailResponse {
  id: number;
  source: SupplementKnowledge.Source;
  conversationId: number;
  itemIdentifier: SupplementKnowledgeRelatedSPU;
  supplementalKnowledgeValue: SupplementalKnowledgeValue;
  /** @deprecated 请使用 getSupplementKnowledgeOperationHistory 获取操作记录 */
  timeLines: SupplementKnowledgeDetailTimeline[];
  // 是否可以保存并启用
  enable: boolean;
  aiReply: SupplementalKnowledgeAiReply;
  aiReplyMap?: Record<string, SupplementalKnowledgeAiReply>;
  modify?: boolean;
  aiReviewStatus: SupplementKnowledge.AiReviewStatus;
  manualReviewStatus: SupplementKnowledge.ManualReviewStatus;
  reviewStatus: SupplementKnowledge.ReviewStatus | SupplementKnowledge.AiErrorReplyReviewStatus;
  pointId: string;
  expandCount?: {
    spuCount: number;
    skuCount: number;
  };
  storeInfo?: SupplementKnowledgeStoreInfo;
  submittedTime: number;
  submittedUserName: string;
  testReqId?: string;
}

export const getSupplementKnowledgeDetail = (id: number, signal?: AbortSignal) => {
  return resolve(
    leyanbotClient.get<ImResponse<SupplementKnowledgeDetailResponse>>(
      `/oversea-knowledge/supplemental_knowledge/detail/${id}`,
      {
        signal,
      },
    ),
  );
};

export interface GetSupplementKnowledgeDetailByMsgIdParams {
  conversationId: string;
  msgId: string;
  source?: SupplementKnowledge.Source;
}

export const getSupplementKnowledgeDetailByMsgId = (
  params: GetSupplementKnowledgeDetailByMsgIdParams,
  signal?: AbortSignal,
) => {
  return resolve(
    leyanbotClient.get<ImResponse<SupplementKnowledgeDetailResponse>>(
      `/oversea-knowledge/supplemental_knowledge/detail_by_msg_id`,
      {
        params,
        signal,
      },
    ),
  );
};

// 更新开关配置
export const updateSupplementKnowledgeConfig = (enable: boolean) => {
  return leyanbotClient.post<ImResponse<null>>(
    `/oversea-knowledge/supplemental_knowledge/config/update`,
    {
      aiAutoReviewConfigEnable: enable,
    },
  );
};

export const getSupplementKnowledgeConfig = () => {
  return leyanbotClient.get<
    ImResponse<{
      aiAutoReviewConfigEnable: boolean;
    }>
  >(`/oversea-knowledge/supplemental_knowledge/config/query`);
};

export interface SupplementKnowledgeListSubmittedUser {
  id: number;
  name: string;
  account: string;
  phone: string;
  email: string;
  status: number;
}

export const getSupplementKnowledgeListSubmittedUser = (signal?: AbortSignal) => {
  return leyanbotClient.get<ImResponse<SupplementKnowledgeListSubmittedUser[]>>(
    '/oversea-knowledge/supplemental_knowledge/list_submitted_user',
    {
      signal,
    },
  );
};

export interface UpdateSupplementKnowledgeBody {
  id?: number;
  ids?: number[];
  manualReviewStatus?:
    | SupplementKnowledge.ManualReviewStatus
    | SupplementKnowledge.AiErrorReplyReviewStatus;
  answer?: string;
  enable?: boolean;
  supplementalKnowledgeValue?: {
    answers?: SupplementKnowledgeAnswer[];
    question?: {
      messageId: string;
      message: string;
    };
    rewriteQuestion?: string;
  };
  modify?: boolean;
}

export const updateSupplementKnowledge = (body: UpdateSupplementKnowledgeBody) => {
  return leyanbotClient.post<ImResponse<null>>(
    `/oversea-knowledge/supplemental_knowledge/update`,
    body,
  );
};

export interface UpdateSupplementKnowledgeV2Body {
  id: number;
  manualReviewStatus?:
    | SupplementKnowledge.ManualReviewStatus
    | SupplementKnowledge.AiErrorReplyReviewStatus;
  modify?: boolean;
  supplementalKnowledgeValue?: {
    rewriteQuestion?: string;
    originQuestions?: SupplementalKnowledgeValueMessage[];
    originAnswers?: SupplementKnowledgeAnswer[];
    botQaMap?: Record<string, SupplementKnowledgeValueMessage[]>;
    rewriteAnswers?: SupplementKnowledgeAnswer[];
  };
}

export const updateSupplementKnowledgeV2 = (body: UpdateSupplementKnowledgeV2Body) => {
  return leyanbotClient.post<ImResponse<null>>(
    `/oversea-knowledge/supplemental_knowledge/update/v2`,
    body,
  );
};

export const updateSupplementKnowledgeRemark = (id: number, remark: string) => {
  return leyanbotClient.post<ImResponse<null>>(
    `/oversea-knowledge/supplemental_knowledge/submit_remark`,
    {
      id,
      remark,
    },
  );
};

export const clearKnowledgeQuery = (conversationId: string) => {
  return doorClient.post<ImResponse<null>>(`/oversea-conversation/v1/clearKnowledgeQuery`, {
    conversationId,
  });
};

export interface SupplementKnowledgeAnswer {
  messageId?: string;
  message?: string;
  url?: string;
  localUrl?: string;
  thumbUrl?: string;
  thumbWidth?: string;
  thumbHeight?: string;
  durationSeconds?: string;
  msgType: 'TEXT' | 'IMAGE' | 'VIDEO';
  translateMap?: Record<string, string>;
}

export interface CreateSupplementKnowledgeBody {
  conversationId: string;
  spuId: string;
  storeId: string;
  skuId?: string;
  skuIds?: string[];
  supplementalKnowledgeValue: {
    answersMsgIds?: string[];
    question: {
      message: string;
      messageId: string;
    };
    answer?: SupplementKnowledgeAnswer;
    answers: SupplementKnowledgeAnswer[];
    buyerNick: string;
    rewriteQuery: string;
  };
  /**默认 CUSTOMER 表示原有的知识 AI_ERROR 表示 ai 回复错误 */
  source?: SupplementKnowledge.Source;
  manualReviewStatus?:
    | SupplementKnowledge.ManualReviewStatus
    | SupplementKnowledge.AiErrorReplyReviewStatus;
  modify?: boolean;
  recreate?: boolean;
}

// 创建补充知识
export const createSupplementKnowledge = (body: CreateSupplementKnowledgeBody) => {
  return leyanbotClient.post<ImResponse<null>>(
    '/oversea-knowledge/supplemental_knowledge/create',
    body,
  );
};

export interface GetSupplementKnowledgeOriginalSpuParams {
  knowledgeId: number;
  pageNo: number;
  pageSize: number;
}

export interface SpuItem {
  platform: string;
  shopId: string;
  shopName: string;
  shopAlias: string;
  region: string;
  spuId: string;
  title: string;
  imageUrl: string;
  itemUrl: string;
  itemInternalId: string;
  sellerSpu: string;
  storeId: string;
  price: number;
  unit: string;
  selected: boolean;
  skus: {
    sellerSku: string;
    skuId: string;
    skuName: string;
    selected: boolean;
  }[];
}

export interface SupplementKnowledgeOriginalSpuItem {
  skuGroupHashKey: number;
  originalSpuList: SpuItem[];
  expandSpuList: SpuItem[];
  expandCount: {
    spuCount: number;
    skuCount: number;
  };
  expandSpuIsCoversAll: boolean;
}

export interface GetSupplementKnowledgeOriginalSpuResponse {
  skuGroupSpuInfoList: SupplementKnowledgeOriginalSpuItem[];
  totalCount: number;
}

/**
 * 获取种子商品组
 */
export const getSupplementKnowledgeOriginalSpu = (
  params: GetSupplementKnowledgeOriginalSpuParams,
) => {
  return leyanbotClient.get<ImResponse<GetSupplementKnowledgeOriginalSpuResponse>>(
    '/oversea-knowledge/supplemental_knowledge/original_spu',
    {
      params,
    },
  );
};

interface RemoveSupplementKnowledgeOriginalSpuParams {
  knowledgeId: number;
  skuGroupHashKey: number;
  originalSpuList: {
    spuId: string;
    platform: string;
    region: string;
    skus: { skuId: string }[];
  }[];
}

/**
 * 移除种子组
 */
export const removeSupplementKnowledgeOriginalSpu = (
  params: RemoveSupplementKnowledgeOriginalSpuParams,
) => {
  return leyanbotClient.post<ImResponse<null>>(
    '/oversea-knowledge/supplemental_knowledge/remove_sku_group',
    params,
  );
};

export interface UpdateSupplementKnowledgeExpandSpuParams {
  knowledgeId: number;
  action: 'ADD' | 'REMOVE' | 'CREATE_EXTRA_ANSWER' | 'CREATE_ORIGINAL_SPU';
  spuList: {
    spuId: string;
    region: string;
    platform: string;
    skus: {
      skuId: string;
    }[];
  }[];
}

/**
 * 拓展商品移除/新增一个新答案
 */
export const updateSupplementKnowledgeExpandSpu = (
  params: UpdateSupplementKnowledgeExpandSpuParams,
) => {
  return leyanbotClient.post<ImResponse<null>>(
    '/oversea-knowledge/supplemental_knowledge/update_expand_spu',
    params,
  );
};

/**
 * 查询单品id name
 */
export const getSupplementKnowledgeSpuClassList = () => {
  return resolve(
    leyanbotClient.get<ImResponse<Array<{ id: string; name: string }>>>(
      '/oversea-knowledge/knowledge/spu_class/list',
    ),
  );
};

export const getSupplementKnowledgeStatusStatistics = () => {
  return leyanbotClient.get<
    ImResponse<{
      waitHumanReviewCount: number;
      maxWaitMachineReviewCount: number;
    }>
  >('/oversea-knowledge/supplemental_knowledge/status_statistics');
};

export interface UpdateSeedSkuBody {
  knowledgeId: number;
  spuId: string;
  skuIds: string[];
}

export const updateSeedSku = (body: UpdateSeedSkuBody) => {
  return leyanbotClient.post<ImResponse<null>>(
    '/oversea-knowledge/supplemental_knowledge/update_original_spu',
    body,
  );
};

export interface QuitCreateSupplementKnowledgeParams {
  query_msg_id: string;
  popup_times: number;
  assistant_id?: number;
  timestamp: number;
  trigger_popup?: boolean;
  trigger_popup_msg?: string;
  /** 是否存在关联知识库弹窗 */
  exist_popup?: boolean;
  extra?: {
    persist?: boolean;
    exit?: boolean;
  };
}

export const quitCreateSupplementKnowledge = (body: QuitCreateSupplementKnowledgeParams) => {
  return doorClient.post<ImResponse<null>>('/oversea-knowledge-stats/v1/quitSupplyKnowledge', body);
};

interface PersistSupplementKnowledgePopupParams {
  conversationId: string;
  queryMsgId: string;
  timestampInMs: string;
}

export const persistSupplementKnowledgePopup = (params: PersistSupplementKnowledgePopupParams) => {
  return doorClient.post<ImResponse<null>>(
    '/oversea-conversation/v1/persistKnowledgePopup',
    params,
  );
};

export interface SaveKnowledge
  extends Pick<
    SupplementalKnowledgeAiReplyKnowledge,
    'knowledgeId' | 'key' | 'value' | 'source' | 'bindItem' | 'kvId'
  > {
  pointId?: string;
  operate: 'CREATE' | 'UPDATE' | 'DELETE' | 'DISABLE';
  isDefault?: boolean;
}

export const saveSupplementalKnowledge = (body: {
  knowledgeList: SaveKnowledge[];
  testReqId?: string;
  knowledgeId: number;
}) => {
  return leyanbotClient.post<ImResponse<{ needUpdate: boolean }>>(
    '/oversea-knowledge/supplemental_knowledge/save',
    body,
  );
};

export type TestSupplementalKnowledgeData = SupplementalKnowledgeAiReply & { isH2h: boolean };

export const testSupplementalKnowledge = (body: {
  knowledgeId?: number;
  knowledgeList?: SaveKnowledge[];
  storeId: string;
  /** 买家问题ID */
  msgId: string;
}) => {
  return leyanbotClient.post<ImResponse<TestSupplementalKnowledgeData>>(
    '/oversea-knowledge/supplemental_knowledge/test',
    body,
  );
};

export const getSupplementalCount = () => {
  return resolve(
    leyanbotClient.post<
      ImResponse<{
        customerCount: number;
        aiErrorReplyCount: number;
      }>
    >('/oversea-knowledge/supplemental_knowledge/count', null),
  );
};

export const getSourceKnowledgeUrl = (fileName: string) => {
  return leyanbotClient.get<ImResponse<string>>(
    `/oversea-knowledge/product_detail_knowledge/raw_data/get_download_url/${fileName}`,
  );
};

export const getQueryReplyByConversationId = (conversationId: string, signal?: AbortSignal) => {
  return leyanbotClient.get<
    ImResponse<
      Array<{
        queryMsgId: number;
        queryMsgIds?: number[];
        answersMsgIds?: number[];
      }>
    >
  >(`/oversea-knowledge/supplemental_knowledge/msgId/${conversationId}`, {
    signal,
  });
};

export const getExpandSpus = (body: SupplementKnowledgeRelatedSPU[]) => {
  return resolve(
    leyanbotClient.post<ImResponse<SupplementKnowledgeRelatedSPU[]>>(
      '/oversea-knowledge/supplemental_knowledge/expand_spu',
      body,
    ),
  );
};

export interface SupplementKnowledgeValueMessage {
  messageId?: string;
  message?: string;
  url?: string;
  localUrl?: string;
  thumbUrl?: string;
  thumbWidth?: string;
  thumbHeight?: string;
  durationSeconds?: string;
  msgType: 'TEXT' | 'IMAGE' | 'VIDEO';
  translateMap?: Record<string, string>;
  isBotReply: boolean;
  botReplyType?: Conversation.BotReplyType;
  botReplyMsgId?: string;
  botDialogueReplyType?: Conversation.BotDialogueReplyType;
}

export interface SubmitSupplementKnowledgeBody {
  conversationId: string;
  spuId: string;
  skuIds: string[];
  storeId: string;
  supplementalKnowledgeValue: {
    buyerNick: string;
    originAnswers: SupplementKnowledgeValueMessage[];
    originQuestions: SupplementKnowledgeValueMessage[];
    botQaMap?: Record<string, SupplementKnowledgeValueMessage[]>;
    rewriteAnswers?: SupplementKnowledgeValueMessage[];
  };
  manualReviewStatus?:
    | SupplementKnowledge.ManualReviewStatus
    | SupplementKnowledge.AiErrorReplyReviewStatus;
  modify?: boolean;

  citeKnowledgeSaveReq?: {
    knowledgeList: SaveKnowledge[];
    testReqId?: string;
  };
}

export const submitSupplementKnowledge = (body: SubmitSupplementKnowledgeBody) => {
  return leyanbotClient.post<ImResponse<null>>(
    '/oversea-knowledge/supplemental_knowledge/submit',
    body,
  );
};

/**
 * 获取操作记录
 */
export const getSupplementKnowledgeOperationHistory = (
  knowledgeId: string,
  signal?: AbortSignal,
) => {
  return leyanbotClient.get<ImResponse<SupplementKnowledgeDetailTimeline[]>>(
    `/oversea-knowledge/supplemental_knowledge/operate_history/${knowledgeId}`,
    {
      signal,
    },
  );
};

/**
 * 批量审核
 */
export const batchUpdateSupplementKnowledgeStatus = (body: {
  knowledgeIds: number[];
  manualReviewStatus: 'REVIEW_PASSED' | 'REVIEW_FAILED' | 'REVIEW_SKIP';
}) => {
  return leyanbotClient.post<ImResponse<null>>(
    '/oversea-knowledge/supplemental_knowledge/batch_update_status',
    body,
  );
};

/**
 * 批量分配
 */
export const batchAssignSupplementKnowledge = (body: {
  knowledgeIds: number[];
  assigneeId: number;
}) => {
  return leyanbotClient.post<ImResponse<null>>(
    '/oversea-knowledge/supplemental_knowledge/batch_assign',
    body,
  );
};

interface ReportSupplementKnowledgeEventBody {
  eventCode: 'AI_LEARNING_PROMPT_QUESTION';
  /** 问题消息ID */
  questionMessageId: string;
  /** 店铺ID */
  storeId: string;
}

/**
 * 自学习事件上报
 */
export const reportSupplementKnowledgeEvent = (body: ReportSupplementKnowledgeEventBody) => {
  return leyanbotClient.post<ImResponse<null>>('/oversea-knowledge/knowledge/event_track', body);
};

export interface RewriteSupplementKnowledgeQuestionItem {
  messageId: string;
  message?: string;
  msgType: 'TEXT' | 'IMAGE' | 'VIDEO';
  url?: string;
  localUrl?: string;
  thumbUrl?: string;
  thumbWidth?: string;
  thumbHeight?: string;
  durationSeconds?: string;
}

export interface RewriteSupplementKnowledgeQuestionResponse {
  questions: SupplementalKnowledgeValueMessage[];
  rewriteQuestion: string;
  rewriteQuestionTranslateMap?: Record<string, string>;
}

export const rewriteSupplementKnowledgeQuestion = (
  body: {
    questions: RewriteSupplementKnowledgeQuestionItem[];
  },
  signal?: AbortSignal,
) => {
  return leyanbotClient.post<ImResponse<RewriteSupplementKnowledgeQuestionResponse>>(
    '/oversea-knowledge/supplemental_knowledge/rewrite_question',
    body,
    {
      signal,
    },
  );
};
